<script setup>
import { useSlots } from 'vue';
import { CsText } from '../text';
import { checkboxRecipe } from './checkbox.recipe';

/**
 * @typedef {object} CheckboxProps
 * @property {boolean} [value=false] - The checked state of the checkbox
 * @property {boolean} [isIndeterminate=false] - Whether the checkbox is in an indeterminate state
 * @property {boolean} [isDisabled=false] - Whether the checkbox is disabled
 */

/**
 * @type {CheckboxProps}
 */
const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  isIndeterminate: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['input', 'change']);

const slots = useSlots();
const hasSlot = name => !!slots[name];

function handleCheckboxChange(e) {
  e.stopPropagation();

  emit('input', !props.value);
  emit('change', !props.value);
}

function getStatus() {
  if (props.isDisabled) {
    return 'disabled';
  }

  if (props.value || props.isIndeterminate) {
    return 'active';
  }

  return 'default';
}

const { checkboxRoot } = checkboxRecipe();
</script>

<template>
  <label
    data-component="CsCheckbox"
    class="inline-flex cursor-pointer select-none items-center gap-2"
  >
    <div class="relative">
      <input
        :value="value"
        type="checkbox"
        class="sr-only"
        :disabled="props.isDisabled"
        @change="handleCheckboxChange"
      />

      <div :class="checkboxRoot({ status: getStatus() })">
        <svg
          class="absolute top-1/2 text-white left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition duration-100 ease-out"
          :class="isIndeterminate ? 'scale-100 visible' : 'scale-0 invisible'"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4 8C4 7.58579 4.33579 7.25 4.75 7.25H11.25C11.6642 7.25 12 7.58579 12 8C12 8.41421 11.6642 8.75 11.25 8.75H4.75C4.33579 8.75 4 8.41421 4 8Z"
            fill="white"
          />
        </svg>

        <svg
          class="absolute top-1/2 text-white left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition duration-200 ease-out"
          :class="
            value && !isIndeterminate
              ? 'scale-100 visible'
              : 'scale-0 invisible'
          "
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.0303 7.21967C14.3232 7.51256 14.3232 7.98744 14.0303 8.28033L9.53033 12.7803C9.23744 13.0732 8.76256 13.0732 8.46967 12.7803L6.21967 10.5303C5.92678 10.2374 5.92678 9.76256 6.21967 9.46967C6.51256 9.17678 6.98744 9.17678 7.28033 9.46967L9 11.1893L12.9697 7.21967C13.2626 6.92678 13.7374 6.92678 14.0303 7.21967Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>

    <CsText
      v-if="hasSlot('default')"
      :variant-color="props.isDisabled ? 'disabled' : 'default'"
    >
      <slot />
    </CsText>
  </label>
</template>
